import {
    getCustomLabelsFromVizTableConfig,
    getHiddenFieldsFromVizTableConfig,
    type VizTableConfig,
} from '@lightdash/common';
import { ActionIcon, Popover, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useMemo, type FC } from 'react';
import MantineIcon from '../../../../components/common/MantineIcon';
import ExportResults from '../../../../components/ExportResults';
import { useCanExportProjectData } from '../../../../hooks/useCanExportProjectData';
import { type Limit } from '../../../../components/ExportResults/types';

type Props = {
    projectUuid: string;
    disabled: boolean;
    vizTableConfig?: VizTableConfig;
    columnOrder: string[];
    totalResults: number;
    chartName: string | undefined;
    getDownloadQueryUuid: (
        limit: number | null,
        limitType?: Limit,
        exportPivotedData?: boolean,
    ) => Promise<string>;
};

const ResultsDownloadButton: FC<Props> = ({
    disabled,
    projectUuid,
    vizTableConfig,
    columnOrder,
    totalResults,
    getDownloadQueryUuid,
    chartName,
}) => {
    const canExportData = useCanExportProjectData(projectUuid);
    const [downloadCustomLabels, downloadHiddenColumns] = useMemo(() => {
        const customLabels = getCustomLabelsFromVizTableConfig(vizTableConfig);
        const hiddenColumns = getHiddenFieldsFromVizTableConfig(vizTableConfig);
        return [customLabels, hiddenColumns];
    }, [vizTableConfig]);

    if (!canExportData) {
        return null;
    }

    return (
        <Popover withArrow disabled={disabled}>
            <Popover.Target>
                <Tooltip variant="xs" label="Download results">
                    <ActionIcon variant="default" disabled={disabled}>
                        <MantineIcon icon={IconDownload} />
                    </ActionIcon>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
                <ExportResults
                    projectUuid={projectUuid}
                    totalResults={totalResults}
                    getDownloadQueryUuid={getDownloadQueryUuid}
                    hiddenFields={downloadHiddenColumns}
                    chartName={chartName}
                    customLabels={downloadCustomLabels}
                    columnOrder={columnOrder}
                />
            </Popover.Dropdown>
        </Popover>
    );
};

export default ResultsDownloadButton;
